/**
 * E2B Sandbox Provider
 *
 * A simplified E2B sandbox implementation that supports mounting
 * cloud filesystems (S3, GCS, R2) via FUSE.
 *
 * @see https://e2b.dev/docs
 */

import type { RequestContext } from '@mastra/core/di';
import type {
  SandboxInfo,
  WorkspaceFilesystem,
  MountResult,
  FilesystemMountConfig,
  ProviderStatus,
  MountManager,
  MastraSandboxOptions,
  SandboxFileInput,
  SandboxNetworking,
  SandboxCloneOptions,
  SandboxStartResult,
} from '@mastra/core/workspace';

/**
 * Inlined from `@mastra/core/workspace` to avoid requiring a newer core peer dep.
 */
type InstructionsOption = string | ((opts: { defaultInstructions: string; requestContext?: RequestContext }) => string);
import { MastraSandbox, SandboxError, SandboxNotReadyError, assertModesUnsupported } from '@mastra/core/workspace';
import { Sandbox, Template } from 'e2b';
import type {
  BuildOptions,
  SandboxConnectOpts,
  SandboxInfo as E2BSandboxListInfo,
  SandboxLifecycle,
  SandboxNetworkOpts,
  SandboxOpts,
  TemplateBuilder,
  TemplateClass,
} from 'e2b';
import { createDefaultMountableTemplate, isDeferredNamedTemplateSpec, isNamedTemplateSpec } from '../utils/template';
import type { DeferredNamedTemplateSpec, NamedTemplateSpec, TemplateResources, TemplateSpec } from '../utils/template';
import { mountS3, mountGCS, mountAzure, LOG_PREFIX } from './mounts';
import type {
  E2BMountConfig,
  E2BS3MountConfig,
  E2BGCSMountConfig,
  E2BAzureBlobMountConfig,
  MountContext,
} from './mounts';
import { E2BProcessManager } from './process-manager';

/** Allowlist pattern for mount paths — absolute path with safe characters only. */
const SAFE_MOUNT_PATH = /^\/[a-zA-Z0-9_.\-/]+$/;

function validateMountPath(mountPath: string): void {
  if (!SAFE_MOUNT_PATH.test(mountPath)) {
    throw new Error(
      `Invalid mount path: ${mountPath}. Must be an absolute path with alphanumeric, dash, dot, underscore, or slash characters only.`,
    );
  }
}

/** Allowlist for marker filenames from ls output — e.g. "mount-abc123" */
const SAFE_MARKER_NAME = /^mount-[a-z0-9]+$/;

/**
 * Per-process dedupe of background template rebuild triggers, keyed by
 * template ref. Retained on successful trigger (the ref only ever needs one
 * build; once it exists the exists-check short-circuits before this path),
 * cleared on trigger failure so a later start can retry.
 */
const inFlightBackgroundBuilds = new Set<string>();

// =============================================================================
// E2B Sandbox Options
// =============================================================================

/**
 * E2B sandbox provider configuration.
 */
export interface E2BSandboxOptions extends Omit<MastraSandboxOptions, 'processes'> {
  /** Unique identifier for this sandbox instance */
  id?: string;
  /**
   * Persisted E2B provider sandbox ID to reattach to deterministically.
   *
   * When set, `start()` first queries this exact sandbox and connects to it
   * (resuming it if paused) instead of discovering by logical `id` metadata.
   * Only a typed "sandbox gone" error (not found / killed / not running)
   * falls through to the usual logical-id lookup and create ladder;
   * auth, quota, rate-limit, timeout, and network errors propagate without
   * creating a new sandbox.
   */
  sandboxId?: string;
  /**
   * What `start()` does when `sandboxId` is set but E2B reports that sandbox
   * is gone (not found / killed / not running).
   *
   * - `'best-effort'` (default): fall back to logical-id discovery, then
   *   create a new sandbox.
   * - `'required'`: throw a `SandboxError` with code `SANDBOX_REATTACH_FAILED`
   *   instead of discovering or creating, so the caller can surface the lost VM.
   */
  reattach?: 'best-effort' | 'required';
  /**
   * Sandbox template specification.
   *
   * - `string` - Use an existing template by ID
   * - `TemplateBuilder` - Use a custom template (e.g., from `createMountableTemplate()`)
   * - `(base) => base.aptInstall([...])` - Customize the default mountable template
   *
   * If not provided and mounting is used, a default template with s3fs will be built.
   * For best performance, pre-build your template and use the template ID.
   *
   * @see createDefaultMountableTemplate
   */
  template?: TemplateSpec;
  /** Sandbox lifetime in milliseconds: how long E2B keeps the VM alive before stopping it
   *
   * @default 300_000 // 5 minutes
   */
  timeout?: number;
  /** Environment variables to set in the sandbox */
  env?: Record<string, string>;
  /** Custom metadata */
  metadata?: Record<string, unknown>;
  /** Network configuration to use when creating the E2B sandbox */
  network?: SandboxNetworkOpts;
  /**
   * Sandbox lifecycle behavior when the `timeout` is reached.
   *
   * Defaults to `{ onTimeout: 'pause' }`, which snapshots the sandbox so the
   * next `start()` reconnects and resumes it. Pass `{ onTimeout: 'kill' }` for
   * stateless workspaces whose data lives outside the sandbox (e.g. mounted
   * from S3) — idle sandboxes are then destroyed and recreated on next use
   * instead of retained as paused snapshots.
   *
   * Note: an explicit `stop()` always pauses, regardless of this setting.
   */
  lifecycle?: SandboxLifecycle;

  /** Domain for self-hosted E2B. Falls back to E2B_DOMAIN env var. */
  domain?: string;
  /** API URL for self-hosted E2B. Falls back to E2B_API_URL env var. */
  apiUrl?: string;
  /** API key for authentication. Falls back to E2B_API_KEY env var. */
  apiKey?: string;
  /** Access token for authentication. Falls back to E2B_ACCESS_TOKEN env var. */
  accessToken?: string;
  /**
   * Custom instructions that override the default instructions
   * returned by `getInstructions()`.
   *
   * - `string` — Fully replaces the default instructions.
   *   Pass an empty string to suppress instructions entirely.
   * - `(opts) => string` — Receives the default instructions and
   *   optional request context so you can extend or customise per-request.
   */
  instructions?: InstructionsOption;
}

// =============================================================================
// E2B Sandbox Implementation
// =============================================================================

/**
 * Simplified E2B sandbox implementation.
 *
 * Features:
 * - Single sandbox instance lifecycle
 * - Supports mounting cloud filesystems (S3, GCS, R2) via FUSE
 * - Automatic sandbox timeout handling with retry
 *
 * @example Basic usage
 * ```typescript
 * import { Workspace } from '@mastra/core/workspace';
 * import { E2BSandbox } from '@mastra/e2b';
 *
 * const sandbox = new E2BSandbox({
 *   timeout: 60000,
 * });
 *
 * const workspace = new Workspace({ sandbox });
 * const result = await workspace.executeCode('console.log("Hello!")');
 * ```
 *
 * @example With S3 filesystem mounting
 * ```typescript
 * import { Workspace } from '@mastra/core/workspace';
 * import { E2BSandbox } from '@mastra/e2b';
 * import { S3Filesystem } from '@mastra/s3';
 *
 * const workspace = new Workspace({
 *   mounts: {
 *     '/bucket': new S3Filesystem({
 *       bucket: 'my-bucket',
 *       region: 'us-east-1',
 *     }),
 *   },
 *   sandbox: new E2BSandbox({ timeout: 60000 }),
 * });
 *
 * ```
 */
export class E2BSandbox extends MastraSandbox<Sandbox> {
  readonly id: string;
  readonly name: string = 'E2BSandbox';
  readonly provider: string = 'e2b';
  status: ProviderStatus = 'pending';

  declare readonly mounts: MountManager; // Non-optional (initialized by BaseSandbox)
  declare readonly processes: E2BProcessManager;

  /**
   * Networking capability: public HTTPS URLs for sandbox ports.
   * E2B exposes every port via `getHost(port)` — no upfront declaration needed.
   *
   * When not attached in this process, the URL is resolved by looking up the
   * existing sandbox by identity (without resuming it) and deriving the host
   * (`{port}-{sandboxId}.{domain}`), so other processes can resolve
   * deployments without waking a paused sandbox.
   */
  readonly networking: SandboxNetworking = {
    getPortUrl: async (port: number): Promise<string | null> => {
      try {
        if (this._sandbox) {
          return `https://${this._sandbox.getHost(port)}`;
        }
        const info = await this.lookupExistingSandboxInfo();
        if (!info) return null;
        return `https://${port}-${info.sandboxId}.${this.sandboxDomain}`;
      } catch {
        return null;
      }
    },
  };

  protected _sandbox: Sandbox | null = null;
  private _createdAt: Date | null = null;
  private _isRetrying = false;
  private readonly timeout: number;
  protected readonly templateSpec?: TemplateSpec;
  private readonly metadata: Record<string, unknown>;
  private readonly network?: SandboxNetworkOpts;
  private readonly lifecycle: SandboxLifecycle;
  protected readonly connectionOpts: Record<string, string>;
  private readonly _preferredSandboxId?: string;
  private readonly _instructionsOverride?: InstructionsOption;
  private readonly _constructorOptions: E2BSandboxOptions;

  /**
   * Resolved template ID after building (if needed). The single cache for
   * template resolution: `resolveTemplate()` returns it when set, and the
   * create-time fallback ladder rewrites it to whichever template actually
   * produced a sandbox.
   *
   * `protected` so a subclass with its own default template (e.g. desktop
   * sandboxes) shares the same cache when it overrides `resolveTemplate()`.
   */
  protected _resolvedTemplateId?: string;
  /**
   * The named spec a deferred template spec resolved to — kept so the
   * 404-on-create fallback ladder can walk the same name/fallback rungs it
   * would for a plain named spec.
   */
  private _resolvedNamedSpec?: NamedTemplateSpec;

  constructor(options: E2BSandboxOptions = {}) {
    super({
      ...options,
      name: 'E2BSandbox',
      processes: new E2BProcessManager({
        defaultTimeout: options.timeout ?? 300_000,
      }),
    });

    this.id = options.id ?? this.generateId();
    this.timeout = options.timeout ?? 300_000; // 5 minutes;
    this.templateSpec = options.template;
    this.metadata = options.metadata ?? {};
    this.network = options.network;
    // Always sent explicitly: the E2B API defaults to 'kill' when lifecycle is omitted.
    this.lifecycle = options.lifecycle ?? { onTimeout: 'pause' };
    this.connectionOpts = {
      ...(options.domain && { domain: options.domain }),
      ...(options.apiUrl && { apiUrl: options.apiUrl }),
      ...(options.apiKey && { apiKey: options.apiKey }),
      ...(options.accessToken && { accessToken: options.accessToken }),
    };

    this._preferredSandboxId = options.sandboxId;
    this._instructionsOverride = options.instructions;
    this._constructorOptions = { ...options };
  }

  /**
   * Construct a sibling `E2BSandbox` that inherits this sandbox's
   * configuration (credentials, template, network, metadata, instructions)
   * with per-instance overrides.
   *
   * Performs no I/O — the sandbox clone provisions (or reconnects to an
   * existing E2B sandbox with the same logical `id`) on its own `start()`.
   * Use it when one configured sandbox acts as the template for a fleet of
   * independent sandboxes (e.g. one per project).
   *
   * `options.idleTimeoutMinutes` maps to the E2B sandbox `timeout` (ms);
   * `options.sandboxId` reattaches the clone to that exact E2B sandbox on
   * `start()`. The parent's own preferred provider sandbox ID is never
   * inherited — physical identity is per-instance.
   */
  clone(options: SandboxCloneOptions = {}): E2BSandbox {
    const { id: _id, sandboxId: _sandboxId, ...base } = this._constructorOptions;
    return new E2BSandbox({
      ...base,
      ...(options.id !== undefined && { id: options.id }),
      ...(options.sandboxId !== undefined && { sandboxId: options.sandboxId }),
      ...(options.env !== undefined && { env: options.env }),
      ...(options.idleTimeoutMinutes !== undefined && { timeout: options.idleTimeoutMinutes * 60_000 }),
    });
  }

  /**
   * Get the underlying E2B Sandbox instance for direct access to E2B APIs.
   *
   * Use this when you need to access E2B features not exposed through the
   * WorkspaceSandbox interface (e.g., files API, ports, etc.).
   *
   * @throws {SandboxNotReadyError} If the sandbox has not been started
   *
   * @example Direct file operations
   * ```typescript
   * const e2b = sandbox.e2b;
   * await e2b.files.write('/tmp/test.txt', 'Hello');
   * const content = await e2b.files.read('/tmp/test.txt');
   * const files = await e2b.files.list('/tmp');
   * ```
   *
   * @example Access ports
   * ```typescript
   * const e2b = sandbox.e2b;
   * const url = e2b.getHost(3000);
   * ```
   */
  get e2b(): Sandbox {
    if (!this._sandbox) {
      throw new SandboxNotReadyError(this.id);
    }
    return this._sandbox;
  }

  /**
   * The E2B provider sandbox ID resolved after connect or create.
   *
   * Persist this to reattach deterministically later via the `sandboxId`
   * option (or `clone({ sandboxId })`). Undefined until the sandbox has been
   * started (attached) in this process.
   */
  get sandboxId(): string | undefined {
    return this._sandbox?.sandboxId;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  /**
   * Acquisition primitives (base-orchestrated start): the base derives
   * `outcome: 'created'` only when a brand-new sandbox VM was created;
   * reconnecting (including resuming a paused sandbox) is `outcome: 'connected'`.
   *
   * `find` returns an already-connected E2B handle: `Sandbox.connect`
   * resumes paused sandboxes, and its failures are deliberately swallowed
   * (unusable handle → create fresh) — that forgiveness is this provider's
   * policy, so it lives here rather than in `connect`. The exception is the
   * `sandboxId` reattach inside {@link acquireExistingSandbox}, which is
   * fail-closed: only a "sandbox gone" error falls through to discovery.
   */
  protected override async find(): Promise<Sandbox | undefined> {
    // Already have a sandbox instance
    if (this._sandbox) {
      return this._sandbox;
    }
    return (await this.acquireExistingSandbox()) ?? undefined;
  }

  protected override async connect(existingSandbox: Sandbox): Promise<void> {
    if (existingSandbox === this._sandbox) {
      return;
    }
    this._sandbox = existingSandbox;
    this._createdAt = new Date();
    this.logger.debug(`${LOG_PREFIX} Reconnected to existing sandbox for: ${this.id}`);

    // Clean up stale mounts from previous config
    // (processPending is called by base class after start completes)
    const expectedPaths = Array.from(this.mounts.entries.keys());
    this.logger.debug(`${LOG_PREFIX} Running mount reconciliation...`);
    await this.reconcileMounts(expectedPaths);
    this.logger.debug(`${LOG_PREFIX} Mount reconciliation complete`);
  }

  protected override async create(): Promise<void> {
    // Template resolution happens here — never at construction or during a
    // reconnect — so a sandbox that only ever resumes never triggers a
    // template build. `resolveTemplate()` caches via `_resolvedTemplateId`.
    const resolvedTemplateId = await this.resolveTemplate();

    // Create a new sandbox with our logical ID in metadata.
    // lifecycle defaults to onTimeout: 'pause', which pauses the sandbox on timeout instead of
    // destroying it so the next start() can resume it. Callers can override it (e.g. 'kill').
    this.logger.debug(`${LOG_PREFIX} Creating new sandbox for: ${this.id} with template: ${resolvedTemplateId}`);

    const createOpts: SandboxOpts = {
      ...this.connectionOpts,
      lifecycle: this.lifecycle,
      metadata: {
        ...this.metadata,
        'mastra-sandbox-id': this.id,
      },
      ...(this.network && { network: this.network }),
      timeoutMs: this.timeout,
    };
    // Every rung of the fallback ladder goes through `createSdkSandbox` so a
    // provider layered on the E2B SDK (e.g. `@e2b/desktop`) keeps its override
    // on the retries, not just on the first attempt.
    const createFromTemplate = (templateId: string) => this.createSdkSandbox(templateId, createOpts);
    // A 404 from create means the template id cannot produce a sandbox:
    // deleted between resolve and create, or the name was registered by a
    // FAILED build — E2B keeps a failed build's name visible to
    // `Template.exists`, so a broken name would otherwise be reused forever.
    // Only 404s trigger a fallback retry; auth, quota, and network errors
    // propagate (an ambiguous timeout must not create a duplicate VM).
    const isTemplateUnusable = (error: unknown) => String(error).includes('404');

    let sdkSandbox: Sandbox;
    try {
      sdkSandbox = await createFromTemplate(resolvedTemplateId);
    } catch (createError) {
      if (!isTemplateUnusable(createError)) throw createError;

      const namedSpec =
        this.templateSpec && isNamedTemplateSpec(this.templateSpec) ? this.templateSpec : this._resolvedNamedSpec;
      if (namedSpec) {
        const { fallbackTemplate } = namedSpec;
        if (fallbackTemplate === false) {
          this._resolvedTemplateId = undefined;
          this.logger.error(
            `${LOG_PREFIX} Creating from '${resolvedTemplateId}' failed (fallback disabled): ${createError}`,
          );
          throw createError;
        }
        // Bounded ladder: broken name → named fallback → default mountable
        // template. Every rung only advances on a template-unusable error, so
        // a broken build never wedges the session on a dead name.
        this.logger.warn(
          `${LOG_PREFIX} Creating from '${resolvedTemplateId}' failed, retrying on fallback: ${createError}`,
        );
        this._resolvedTemplateId = undefined;
        const spec = namedSpec;
        const fallbackId =
          resolvedTemplateId === spec.ref
            ? await this.resolveFallbackTemplate(fallbackTemplate)
            : await this.buildOrReuseDefaultTemplate();
        try {
          sdkSandbox = await createFromTemplate(fallbackId);
          // Cache coherence: later creates on this instance (e.g. after the
          // VM died) must reuse the template that actually worked, not
          // re-walk the ladder from the broken name.
          this._resolvedTemplateId = fallbackId;
        } catch (fallbackError) {
          if (!isTemplateUnusable(fallbackError)) throw fallbackError;
          // Terminal recovery: the default name itself may be registered by
          // a FAILED build. Force-rebuild it once (mirrors the no-spec
          // path's 404 recovery) — past this, the error propagates.
          const rebuildDefaultAndCreate = async (): Promise<Sandbox> => {
            this.logger.warn(`${LOG_PREFIX} Default template broken too, rebuilding: ${fallbackError}`);
            const rebuiltId = await this.buildDefaultTemplate();
            const rebuilt = await createFromTemplate(rebuiltId);
            this._resolvedTemplateId = rebuiltId;
            return rebuilt;
          };
          const defaultId = await this.buildOrReuseDefaultTemplate();
          if (defaultId === fallbackId) {
            // The failed fallback WAS the default (specs without a named
            // fallback land on it directly) — skip straight to the rebuild.
            sdkSandbox = await rebuildDefaultAndCreate();
          } else {
            this.logger.warn(`${LOG_PREFIX} Fallback '${fallbackId}' failed too, using default: ${fallbackError}`);
            try {
              sdkSandbox = await createFromTemplate(defaultId);
              this._resolvedTemplateId = defaultId;
            } catch (defaultError) {
              if (!isTemplateUnusable(defaultError)) throw defaultError;
              sdkSandbox = await rebuildDefaultAndCreate();
            }
          }
        }
        this.logger.debug(`${LOG_PREFIX} Created sandbox ${sdkSandbox.sandboxId} from fallback for: ${this.id}`);
      } else if (!this.templateSpec) {
        this.logger.debug(`${LOG_PREFIX} Template not found, rebuilding: ${resolvedTemplateId}`);
        this._resolvedTemplateId = undefined; // Clear cached ID to force rebuild
        const rebuiltTemplateId = await this.buildDefaultTemplate();

        this.logger.debug(`${LOG_PREFIX} Retrying sandbox creation with rebuilt template: ${rebuiltTemplateId}`);
        sdkSandbox = await createFromTemplate(rebuiltTemplateId);
      } else {
        throw createError;
      }
    }
    this._sandbox = sdkSandbox;

    this.logger.debug(`${LOG_PREFIX} Created sandbox ${sdkSandbox.sandboxId} for logical ID: ${this.id}`);
    this._createdAt = new Date();
    // Note: processPending is called by base class after start completes
  }

  /**
   * Stop the E2B sandbox by pausing it (snapshot-stop).
   *
   * Pausing freezes the whole VM — filesystem, memory, and running processes —
   * and stops billing immediately. The next `start()` reconnects and resumes it,
   * with background processes still running. Filesystem mounts are unmounted
   * first (FUSE mounts don't survive pause) and reconciled again on start.
   *
   * Status management is handled by the base class.
   */
  async stop(): Promise<void> {
    // Unmount all filesystems before pausing
    // Collect keys first since unmount() mutates the map
    for (const mountPath of [...this.mounts.entries.keys()]) {
      try {
        await this.unmount(mountPath);
      } catch {
        // Best-effort unmount; sandbox may already be dead
      }
    }

    // Pause failures propagate — a sandbox that failed to pause is still
    // running (and billing), so callers must not assume it stopped.
    if (this._sandbox) {
      await this._sandbox.pause();
      this.logger.debug(`${LOG_PREFIX} Paused sandbox ${this._sandbox.sandboxId} for: ${this.id}`);
    } else {
      // Not attached in this process — pause by identity without resuming.
      const info = await this.lookupExistingSandboxInfo();
      if (info?.state === 'running') {
        await Sandbox.pause(info.sandboxId, this.connectionOpts);
        this.logger.debug(`${LOG_PREFIX} Paused detached sandbox ${info.sandboxId} for: ${this.id}`);
      }
    }

    this._sandbox = null;
  }

  /**
   * Destroy the E2B sandbox and clean up all resources.
   * Unmounts filesystems, kills the sandbox, and clears mount state.
   * Status management is handled by the base class.
   */
  async destroy(): Promise<void> {
    if (this._sandbox) {
      // Kill all background processes
      try {
        const procs = await this.processes.list();
        await Promise.all(procs.map(p => this.processes.kill(p.pid)));
      } catch {
        // Best-effort: sandbox may already be dead
      }

      // Unmount all filesystems
      // Collect keys first since unmount() mutates the map
      for (const mountPath of [...this.mounts.entries.keys()]) {
        try {
          await this.unmount(mountPath);
        } catch {
          // Ignore errors during cleanup
        }
      }

      // Kill failures propagate — a sandbox that failed to delete is still
      // alive, so callers must not assume cleanup completed.
      await this._sandbox.kill();

      this._sandbox = null;
    } else {
      // Not attached in this process — kill by identity without resuming.
      const info = await this.lookupExistingSandboxInfo();
      if (info) {
        await Sandbox.kill(info.sandboxId, this.connectionOpts);
        this.logger.debug(`${LOG_PREFIX} Killed detached sandbox ${info.sandboxId} for: ${this.id}`);
      }
    }

    this.mounts.clear();
  }

  async getInfo(): Promise<SandboxInfo> {
    return {
      id: this.id,
      name: this.name,
      provider: this.provider,
      status: this.status,
      createdAt: this._createdAt ?? new Date(),
      mounts: Array.from(this.mounts.entries).map(([path, entry]) => ({
        path,
        filesystem: entry.filesystem?.provider ?? entry.config?.type ?? 'unknown',
      })),
      metadata: {
        ...this.metadata,
        ...(this._sandbox && { sandboxId: this._sandbox.sandboxId }),
      },
    };
  }

  // ---------------------------------------------------------------------------
  // File Upload
  // ---------------------------------------------------------------------------

  /**
   * Bulk-write files into the sandbox filesystem via the SDK's native upload.
   *
   * Per-file permission modes are not supported; an explicit `mode` is
   * rejected rather than silently discarded.
   */
  async writeFiles(files: SandboxFileInput[]): Promise<void> {
    assertModesUnsupported(files, 'E2B');
    await this.ensureRunning();
    await this.e2b.files.write(
      files.map(f => ({
        path: f.path,
        data: typeof f.content === 'string' ? f.content : new Blob([new Uint8Array(f.content)]),
      })),
    );
  }

  /**
   * Get instructions describing this E2B sandbox.
   * Used by agents to understand the execution environment.
   */
  getInstructions(opts?: { requestContext?: RequestContext }): string {
    if (this._instructionsOverride === undefined) return this._getDefaultInstructions();
    if (typeof this._instructionsOverride === 'string') return this._instructionsOverride;
    const defaultInstructions = this._getDefaultInstructions();
    return this._instructionsOverride({ defaultInstructions, requestContext: opts?.requestContext });
  }

  private _getDefaultInstructions(): string {
    const mountCount = this.mounts.entries.size;
    const mountInfo = mountCount > 0 ? ` ${mountCount} filesystem(s) mounted via FUSE.` : '';
    return `Cloud sandbox.${mountInfo}`;
  }

  // ---------------------------------------------------------------------------
  // Mounting
  // ---------------------------------------------------------------------------

  /**
   * Mount a filesystem at a path in the sandbox.
   * Uses FUSE tools (s3fs, gcsfuse) to mount cloud storage.
   */
  async mount(filesystem: WorkspaceFilesystem, mountPath: string): Promise<MountResult> {
    validateMountPath(mountPath);

    if (!this._sandbox) {
      throw new SandboxNotReadyError(this.id);
    }

    this.logger.debug(`${LOG_PREFIX} Mounting "${mountPath}"...`);

    // Get mount config - MountManager validates this exists before calling mount()
    const config = filesystem.getMountConfig?.() as E2BMountConfig | undefined;
    if (!config) {
      const error = `Filesystem "${filesystem.id}" does not provide a mount config`;
      this.logger.error(`${LOG_PREFIX} ${error}`);
      this.mounts.set(mountPath, { filesystem, state: 'error', error });
      return { success: false, mountPath, error };
    }

    // Check if already mounted with matching config (e.g., when reconnecting to existing sandbox)
    const existingMount = await this.checkExistingMount(mountPath, config);
    if (existingMount === 'matching') {
      this.logger.debug(
        `${LOG_PREFIX} Detected existing mount for ${filesystem.provider} ("${filesystem.id}") at "${mountPath}" with correct config, skipping`,
      );
      this.mounts.set(mountPath, { state: 'mounted', config });
      return { success: true, mountPath };
    } else if (existingMount === 'mismatched') {
      // Different config - unmount and re-mount
      this.logger.debug(`${LOG_PREFIX} Config mismatch, unmounting to re-mount with new config...`);
      await this.unmount(mountPath);
    }
    this.logger.debug(`${LOG_PREFIX} Config type: ${config.type}`);

    // Mark as mounting (handles direct mount() calls; MountManager also sets this for processPending)
    this.mounts.set(mountPath, { filesystem, state: 'mounting', config });

    // Check if directory exists and is non-empty (would shadow existing files)
    try {
      const checkResult = await this._sandbox.commands.run(
        `[ -d "${mountPath}" ] && [ "$(ls -A "${mountPath}" 2>/dev/null)" ] && echo "non-empty" || echo "ok"`,
      );
      if (checkResult.stdout.trim() === 'non-empty') {
        const error = `Cannot mount at ${mountPath}: directory exists and is not empty. Mounting would hide existing files. Use a different path or empty the directory first.`;
        this.logger.error(`${LOG_PREFIX} ${error}`);
        this.mounts.set(mountPath, { filesystem, state: 'error', config, error });
        return { success: false, mountPath, error };
      }
    } catch {
      // Check failed, proceed anyway
    }

    // Create mount directory with sudo (for paths outside home dir like /data)
    // Then chown to current user so mount works without issues
    try {
      this.logger.debug(`${LOG_PREFIX} Creating mount directory for ${mountPath}...`);
      const mkdirCommand = `sudo mkdir -p "${mountPath}" && sudo chown $(id -u):$(id -g) "${mountPath}"`;

      this.logger.debug(`${LOG_PREFIX} Running command: ${mkdirCommand}`);
      const mkdirResult = await this._sandbox.commands.run(mkdirCommand);

      this.logger.debug(`${LOG_PREFIX} Created mount directory for mount path "${mountPath}":`, mkdirResult);
    } catch (mkdirError) {
      this.logger.debug(`${LOG_PREFIX} mkdir error for "${mountPath}":`, mkdirError);
      this.mounts.set(mountPath, { filesystem, state: 'error', config, error: String(mkdirError) });
      return { success: false, mountPath, error: String(mkdirError) };
    }

    // Create mount context for mount operations
    const mountCtx: MountContext = {
      sandbox: this._sandbox,
      logger: this.logger,
    };

    try {
      switch (config.type) {
        case 's3':
          this.logger.debug(`${LOG_PREFIX} Mounting S3 bucket at ${mountPath}...`);
          await mountS3(mountPath, config as E2BS3MountConfig, mountCtx);
          this.logger.debug(`${LOG_PREFIX} Mounted S3 bucket at ${mountPath}`);
          break;
        case 'gcs':
          this.logger.debug(`${LOG_PREFIX} Mounting GCS bucket at ${mountPath}...`);
          await mountGCS(mountPath, config as E2BGCSMountConfig, mountCtx);
          this.logger.debug(`${LOG_PREFIX} Mounted GCS bucket at ${mountPath}`);
          break;
        case 'azure-blob':
          this.logger.debug(`${LOG_PREFIX} Mounting Azure Blob container at ${mountPath}...`);
          await mountAzure(mountPath, config as E2BAzureBlobMountConfig, mountCtx);
          this.logger.debug(`${LOG_PREFIX} Mounted Azure Blob container at ${mountPath}`);
          break;
        default:
          this.mounts.set(mountPath, {
            filesystem,
            state: 'unsupported',
            config,
            error: `Unsupported mount type: ${(config as FilesystemMountConfig).type}`,
          });
          return {
            success: false,
            mountPath,
            error: `Unsupported mount type: ${(config as FilesystemMountConfig).type}`,
          };
      }
    } catch (error) {
      this.logger.error(
        `${LOG_PREFIX} Error mounting "${filesystem.provider}" (${filesystem.id}) at "${mountPath}":`,
        error,
      );
      this.mounts.set(mountPath, { filesystem, state: 'error', config, error: String(error) });

      // Clean up the directory we created since mount failed
      try {
        await this._sandbox!.commands.run(`sudo rmdir "${mountPath}" 2>/dev/null || true`);
        this.logger.debug(`${LOG_PREFIX} Cleaned up directory after failed mount: ${mountPath}`);
      } catch {
        // Ignore cleanup errors
      }

      return { success: false, mountPath, error: String(error) };
    }

    // Mark as mounted
    this.mounts.set(mountPath, { state: 'mounted', config });

    // Write marker file so we can detect config changes on reconnect
    await this.writeMarkerFile(mountPath);

    this.logger.debug(`${LOG_PREFIX} Mounted ${mountPath}`);
    return { success: true, mountPath };
  }

  /**
   * Unmount a filesystem from a path in the sandbox.
   */
  async unmount(mountPath: string): Promise<void> {
    validateMountPath(mountPath);

    if (!this._sandbox) {
      throw new SandboxNotReadyError(this.id);
    }

    this.logger.debug(`${LOG_PREFIX} Unmounting ${mountPath}...`);

    try {
      // Use fusermount for FUSE mounts, fall back to umount
      const result = await this._sandbox.commands.run(
        `sudo fusermount -u "${mountPath}" 2>/dev/null || sudo umount "${mountPath}"`,
      );
      if (result.exitCode !== 0) {
        this.logger.debug(`${LOG_PREFIX} Unmount warning: ${result.stderr || result.stdout}`);
      }
    } catch (error) {
      this.logger.debug(`${LOG_PREFIX} Unmount error:`, error);
      // Try lazy unmount as last resort
      await this._sandbox.commands.run(`sudo umount -l "${mountPath}" 2>/dev/null || true`);
    }

    this.mounts.delete(mountPath);

    // Clean up marker file
    const filename = this.mounts.markerFilename(mountPath);
    const markerPath = `/tmp/.mastra-mounts/${filename}`;
    await this._sandbox.commands.run(`rm -f "${markerPath}" 2>/dev/null || true`);

    // Remove empty mount directory (only if empty, rmdir fails on non-empty)
    // Use sudo since mount directories outside home (like /data) were created with sudo
    const rmdirResult = await this._sandbox.commands.run(`sudo rmdir "${mountPath}" 2>&1`);
    if (rmdirResult.exitCode === 0) {
      this.logger.debug(`${LOG_PREFIX} Unmounted and removed ${mountPath}`);
    } else {
      this.logger.debug(
        `${LOG_PREFIX} Unmounted ${mountPath} (directory not removed: ${rmdirResult.stderr?.trim() || 'not empty'})`,
      );
    }
  }

  /**
   * Unmount all stale mounts that are not in the expected mounts list.
   * Also cleans up orphaned directories and marker files from failed mount attempts.
   * Call this after reconnecting to an existing sandbox to clean up old mounts.
   */
  async reconcileMounts(expectedMountPaths: string[]): Promise<void> {
    if (!this._sandbox) {
      throw new SandboxNotReadyError(this.id);
    }

    this.logger.debug(`${LOG_PREFIX} Reconciling mounts. Expected paths:`, expectedMountPaths);

    // Get current FUSE mounts in the sandbox
    const mountsResult = await this._sandbox.commands.run(
      `grep -E 'fuse\\.(s3fs|gcsfuse|blobfuse2)' /proc/mounts | awk '{print $2}'`,
    );
    const currentMounts = mountsResult.stdout
      .trim()
      .split('\n')
      .filter(p => p.length > 0);

    this.logger.debug(`${LOG_PREFIX} Current FUSE mounts in sandbox:`, currentMounts);

    // Read our marker files to know which mounts WE created
    const markersResult = await this._sandbox.commands.run(`ls /tmp/.mastra-mounts/ 2>/dev/null || echo ""`);
    const markerFiles = markersResult.stdout
      .trim()
      .split('\n')
      .filter(f => f.length > 0 && SAFE_MARKER_NAME.test(f));

    // Build a map of mount paths → marker filenames for mounts WE created
    const managedMountPaths = new Map<string, string>();
    for (const markerFile of markerFiles) {
      const markerResult = await this._sandbox.commands.run(
        `cat "/tmp/.mastra-mounts/${markerFile}" 2>/dev/null || echo ""`,
      );
      const parsed = this.mounts.parseMarkerContent(markerResult.stdout.trim());
      if (parsed && SAFE_MOUNT_PATH.test(parsed.path)) {
        managedMountPaths.set(parsed.path, markerFile);
      }
    }

    // Find mounts that exist but shouldn't — only unmount if WE created them (have a marker)
    const staleMounts = currentMounts.filter(path => !expectedMountPaths.includes(path));

    for (const stalePath of staleMounts) {
      if (managedMountPaths.has(stalePath)) {
        this.logger.debug(`${LOG_PREFIX} Found stale managed FUSE mount at ${stalePath}, unmounting...`);
        await this.unmount(stalePath);
      } else {
        this.logger.debug(`${LOG_PREFIX} Found external FUSE mount at ${stalePath}, leaving untouched`);
      }
    }

    // Clean up orphaned marker files and empty directories from failed mounts
    try {
      const expectedMarkerFiles = new Set(expectedMountPaths.map(p => this.mounts.markerFilename(p)));

      // Build a reverse map: markerFile → mountPath
      const markerToPath = new Map<string, string>();
      for (const [path, file] of managedMountPaths) {
        markerToPath.set(file, path);
      }

      for (const markerFile of markerFiles) {
        // If this marker file doesn't correspond to an expected mount path, clean it up
        if (!expectedMarkerFiles.has(markerFile)) {
          const mountPath = markerToPath.get(markerFile);

          if (mountPath) {
            // Only clean up directory if not currently FUSE mounted
            if (!currentMounts.includes(mountPath)) {
              this.logger.debug(`${LOG_PREFIX} Cleaning up orphaned marker and directory for ${mountPath}`);

              // Remove marker file
              await this._sandbox.commands.run(`rm -f "/tmp/.mastra-mounts/${markerFile}" 2>/dev/null || true`);

              // Try to remove the directory (will fail if not empty or doesn't exist, which is fine)
              await this._sandbox.commands.run(`sudo rmdir "${mountPath}" 2>/dev/null || true`);
            }
          } else {
            // Malformed marker file - just delete it
            this.logger.debug(`${LOG_PREFIX} Removing malformed marker file: ${markerFile}`);
            await this._sandbox.commands.run(`rm -f "/tmp/.mastra-mounts/${markerFile}" 2>/dev/null || true`);
          }
        }
      }
    } catch {
      // Ignore errors during orphan cleanup
      this.logger.debug(`${LOG_PREFIX} Error during orphan cleanup (non-fatal)`);
    }
  }

  // ---------------------------------------------------------------------------
  // Deprecated
  // ---------------------------------------------------------------------------

  /** @deprecated Use `e2b` instead. */
  get instance(): Sandbox {
    return this.e2b;
  }

  /** @deprecated Use `status === 'running'` instead. */
  async isReady(): Promise<boolean> {
    return this.status === 'running' && this._sandbox !== null;
  }

  // ---------------------------------------------------------------------------
  // Internal Helpers
  // ---------------------------------------------------------------------------

  private generateId(): string {
    return `e2b-sandbox-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  }

  /** Domain used to derive public sandbox hosts (self-hosted E2B or e2b.app). */
  private get sandboxDomain(): string {
    return this.connectionOpts.domain ?? process.env.E2B_DOMAIN ?? 'e2b.app';
  }

  /**
   * Look up an existing sandbox with matching mastra-sandbox-id metadata
   * WITHOUT connecting or resuming it. Returns its list info or null.
   */
  private async lookupExistingSandboxInfo(): Promise<E2BSandboxListInfo | null> {
    try {
      // Query E2B for existing sandbox with our logical ID in metadata
      const paginator = Sandbox.list({
        ...this.connectionOpts,
        query: {
          metadata: { 'mastra-sandbox-id': this.id },
          state: ['running', 'paused'],
        },
      });

      const sandboxes = await paginator.nextItems();

      this.logger.debug(`${LOG_PREFIX} sandboxes:`, sandboxes);

      // Sandbox.list only returns running/paused sandboxes, so no need to filter
      if (sandboxes.length > 0) {
        const existingSandbox = sandboxes[0]!;
        this.logger.debug(
          `${LOG_PREFIX} Found existing sandbox for ${this.id}: ${existingSandbox.sandboxId} (state: ${existingSandbox.state})`,
        );
        return existingSandbox;
      }
    } catch (e) {
      this.logger.debug(`${LOG_PREFIX} Error querying for existing sandbox:`, e);
      // Continue to create new sandbox
    }

    return null;
  }

  /**
   * Acquire an existing sandbox: try the preferred provider sandbox ID first
   * (deterministic reattach), then fall back to logical-id metadata discovery.
   */
  private async acquireExistingSandbox(): Promise<Sandbox | null> {
    if (this._preferredSandboxId) {
      const preferred = await this.connectToPreferredSandbox(this._preferredSandboxId);
      if (preferred) return preferred;
      if (this._constructorOptions.reattach === 'required') {
        throw new SandboxError(
          `E2B sandbox ${this._preferredSandboxId} could not be reattached (gone); refusing to provision a replacement because reattach is 'required'. Clear the persisted sandboxId to start a fresh sandbox.`,
          'SANDBOX_REATTACH_FAILED',
          { provider: 'e2b', sandboxId: this._preferredSandboxId, logicalId: this.id },
        );
      }
    }
    return this.findExistingSandbox();
  }

  /**
   * Deterministically reattach to a sandbox by its E2B provider ID.
   *
   * Fail-closed: only a typed "sandbox gone" error (not found / killed /
   * not running) returns null so the caller can fall through to logical-id
   * discovery or creation. Any other error (auth, quota, rate limit,
   * timeout, network) propagates so a duplicate sandbox is never created.
   *
   * Ownership is validated before connecting: a sandbox tagged with a
   * different `mastra-sandbox-id` is refused (without resuming it).
   * Sandboxes without the tag (created outside Mastra) are attachable.
   */
  private async connectToPreferredSandbox(preferredSandboxId: string): Promise<Sandbox | null> {
    let info: E2BSandboxListInfo;
    try {
      info = await Sandbox.getInfo(preferredSandboxId, this.connectionOpts);
    } catch (e) {
      if (this.isSandboxDeadError(e)) {
        this.logger.debug(
          `${LOG_PREFIX} Preferred sandbox ${preferredSandboxId} is gone, falling back to logical-id discovery:`,
          e,
        );
        return null;
      }
      throw e;
    }

    const owner = info.metadata?.['mastra-sandbox-id'];
    if (owner !== undefined && owner !== this.id) {
      throw new Error(
        `${LOG_PREFIX} Provider sandbox ${preferredSandboxId} belongs to logical sandbox id "${owner}", refusing to attach it to "${this.id}"`,
      );
    }

    try {
      return await Sandbox.connect(preferredSandboxId, { ...this.connectionOpts, timeoutMs: this.timeout });
    } catch (e) {
      // The sandbox can terminate between getInfo and connect.
      if (this.isSandboxDeadError(e)) {
        this.logger.debug(
          `${LOG_PREFIX} Preferred sandbox ${preferredSandboxId} vanished before connect, falling back:`,
          e,
        );
        return null;
      }
      throw e;
    }
  }

  /**
   * Find an existing sandbox with matching mastra-sandbox-id metadata.
   * Returns the connected sandbox if found, null otherwise.
   * Connecting to a paused sandbox resumes it.
   */
  private async findExistingSandbox(): Promise<Sandbox | null> {
    const info = await this.lookupExistingSandboxInfo();
    if (!info) return null;
    try {
      return await this.connectSdkSandbox(info.sandboxId, { ...this.connectionOpts, timeoutMs: this.timeout });
    } catch (e) {
      this.logger.debug(`${LOG_PREFIX} Error connecting to existing sandbox:`, e);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  // SDK Factory Hooks (subclass override points)
  // ---------------------------------------------------------------------------

  /**
   * Create a new SDK sandbox from a resolved template ID.
   *
   * Override point for providers layered on the E2B SDK whose `Sandbox`
   * class extends `e2b`'s (e.g. `@e2b/desktop`): override to call their
   * `Sandbox.create`. Connection options are already spread into `opts`.
   */
  protected async createSdkSandbox(templateId: string, opts: SandboxOpts): Promise<Sandbox> {
    return Sandbox.create(templateId, opts);
  }

  /**
   * Connect to (and resume) an existing SDK sandbox by its E2B sandbox ID.
   * Override point — see {@link createSdkSandbox}.
   */
  protected async connectSdkSandbox(sandboxId: string, opts: SandboxConnectOpts): Promise<Sandbox> {
    return Sandbox.connect(sandboxId, opts);
  }

  /**
   * Resolve the template specification to a template ID.
   *
   * - String: Use as-is (template ID)
   * - TemplateBuilder: Build and return the template ID
   * - Function: Apply to base mountable template, then build
   * - undefined: Use default mountable template (cached)
   *
   * Override point: subclasses with a different default template (e.g.
   * desktop sandboxes) override this and {@link buildDefaultTemplate}.
   */
  protected async resolveTemplate(): Promise<string> {
    // If already resolved, return cached ID
    if (this._resolvedTemplateId) {
      return this._resolvedTemplateId;
    }

    // No template specified - use default mountable template with caching
    if (!this.templateSpec) {
      return await this.buildOrReuseDefaultTemplate();
    }

    // String template ID - use directly
    if (typeof this.templateSpec === 'string') {
      this._resolvedTemplateId = this.templateSpec;
      return this.templateSpec;
    }

    // Named spec (e.g. createRepoTemplate) - lazy build-if-missing under a
    // deterministic name, with a fallback so a failed build degrades to a
    // cold start instead of a wedged session. A deferred spec (sha-less
    // createRepoTemplate) computes its name right before the exists check —
    // pinning to the repo's current default-branch head; a rejection there
    // degrades to the default mountable template like any other resolution
    // failure.
    let spec: Exclude<TemplateSpec, DeferredNamedTemplateSpec>;
    if (isDeferredNamedTemplateSpec(this.templateSpec)) {
      try {
        spec = await this.templateSpec.resolveSpec();
        this._resolvedNamedSpec = spec;
      } catch (error) {
        this.logger.warn(`${LOG_PREFIX} Deferred template spec resolution failed, falling back: ${error}`);
        return await this.resolveFallbackTemplate(undefined);
      }
    } else {
      spec = this.templateSpec;
    }
    if (isNamedTemplateSpec(spec)) {
      const { ref, template: namedTemplate, fallbackTemplate, staleRef, buildTags, buildResources } = spec;
      const buildOpts = {
        ...this.connectionOpts,
        ...(buildTags?.length ? { tags: buildTags } : {}),
        ...buildResources,
      };
      try {
        if (await Template.exists(ref, this.connectionOpts)) {
          this.logger.debug(`${LOG_PREFIX} Using cached template: ${ref}`);
          this._resolvedTemplateId = ref;
          return ref;
        }
        // Stale-build-first: when the exact ref is missing but a previous
        // build exists, boot from it immediately and rebuild the fresh ref
        // in the background — only a template's very first build ever
        // blocks a sandbox start. Runtime setup fast-forwards the slightly
        // stale checkout, so freshness never depends on the template.
        if (staleRef && staleRef !== ref && (await Template.exists(staleRef, this.connectionOpts))) {
          this.logger.debug(`${LOG_PREFIX} Using stale build ${staleRef}; rebuilding ${ref} in background`);
          this.triggerBackgroundBuild(namedTemplate as TemplateClass, ref, buildOpts);
          this._resolvedTemplateId = staleRef;
          return staleRef;
        }
        this.logger.debug(`${LOG_PREFIX} Building template: ${ref}...`);
        const buildResult = await Template.build(namedTemplate as TemplateClass, ref, buildOpts);
        this.logger.debug(`${LOG_PREFIX} Template built: ${buildResult.templateId}`);
        // Resolve to the ref, NOT the raw build id: creating a sandbox from
        // a bare template id looks up its `default` tag, which a
        // tag-qualified build (e.g. `name:sha-<sha>`) never assigns — the
        // create would 404 and needlessly ride the fallback ladder.
        this._resolvedTemplateId = ref;
        return ref;
      } catch (error) {
        if (fallbackTemplate === false) {
          this._resolvedTemplateId = undefined;
          this.logger.error(`${LOG_PREFIX} Template '${ref}' resolution failed (fallback disabled): ${error}`);
          throw error;
        }
        this.logger.warn(`${LOG_PREFIX} Template '${ref}' resolution failed, falling back: ${error}`);
        return await this.resolveFallbackTemplate(fallbackTemplate);
      }
    }
    // TemplateBuilder or function - need to build
    let template: TemplateBuilder;
    let templateName: string;

    if (typeof spec === 'function') {
      // Apply customization function to base mountable template
      const { template: baseTemplate } = createDefaultMountableTemplate();
      template = spec(baseTemplate);
      // Custom templates get unique names since they're modified
      templateName = `mastra-custom-${this.id.replace(/[^a-zA-Z0-9-]/g, '-')}`;
    } else {
      // Use provided TemplateBuilder directly
      template = spec;
      templateName = `mastra-${this.id.replace(/[^a-zA-Z0-9-]/g, '-')}`;
    }

    // Build the template
    this.logger.debug(`${LOG_PREFIX} Building custom template: ${templateName}...`);
    const buildResult = await Template.build(template as TemplateClass, templateName, this.connectionOpts);
    this._resolvedTemplateId = buildResult.templateId;
    this.logger.debug(`${LOG_PREFIX} Template built: ${buildResult.templateId}`);

    return buildResult.templateId;
  }

  /**
   * Resolve the default mountable template: reuse when it exists, build once
   * when it does not.
   */
  /**
   * Resolve a named spec's fallback template. A named fallback gets its own
   * exists-then-build resolution; anything failing past that (including
   * specs without a fallback, e.g. repo templates) lands on the default
   * mountable template so a broken build never wedges a session.
   */
  /**
   * Trigger a non-blocking template rebuild via `Template.buildInBackground`
   * (the build runs on E2B's side, so it outlives this process). Deduped
   * per-process by ref so concurrent session starts on the same moved head
   * don't stack duplicate builds; a failed TRIGGER clears the guard so a
   * later start retries. A build that fails server-side simply never
   * registers the ref — the next start falls back to the stale build again
   * and re-triggers.
   */
  private triggerBackgroundBuild(template: TemplateClass, ref: string, buildOpts: Omit<BuildOptions, 'alias'>): void {
    if (inFlightBackgroundBuilds.has(ref)) return;
    inFlightBackgroundBuilds.add(ref);
    void Template.buildInBackground(template, ref, buildOpts)
      .then(result => {
        this.logger.debug(`${LOG_PREFIX} Background template build triggered: ${ref} (${result.buildId})`);
      })
      .catch(error => {
        inFlightBackgroundBuilds.delete(ref);
        this.logger.warn(`${LOG_PREFIX} Background template build trigger failed for '${ref}': ${error}`);
      });
  }

  private async resolveFallbackTemplate(
    fallbackTemplate: Exclude<NamedTemplateSpec['fallbackTemplate'], false>,
  ): Promise<string> {
    if (typeof fallbackTemplate === 'string') {
      this._resolvedTemplateId = fallbackTemplate;
      return fallbackTemplate;
    }
    if (fallbackTemplate && isNamedTemplateSpec(fallbackTemplate)) {
      try {
        if (await Template.exists(fallbackTemplate.ref, this.connectionOpts)) {
          this._resolvedTemplateId = fallbackTemplate.ref;
          return fallbackTemplate.ref;
        }
        const buildResult = await Template.build(fallbackTemplate.template as TemplateClass, fallbackTemplate.ref, {
          ...this.connectionOpts,
          ...fallbackTemplate.buildResources,
        });
        this._resolvedTemplateId = buildResult.templateId;
        return buildResult.templateId;
      } catch (error) {
        this.logger.warn(`${LOG_PREFIX} Fallback template '${fallbackTemplate.ref}' failed too: ${error}`);
        return await this.buildOrReuseDefaultTemplate();
      }
    }
    if (fallbackTemplate) {
      try {
        const buildResult = await Template.build(
          fallbackTemplate as unknown as TemplateClass,
          `mastra-fallback-${this.id.replace(/[^a-zA-Z0-9-]/g, '-')}`,
          this.connectionOpts,
        );
        this._resolvedTemplateId = buildResult.templateId;
        return buildResult.templateId;
      } catch (error) {
        this.logger.warn(`${LOG_PREFIX} Fallback template build failed, using default: ${error}`);
        return await this.buildOrReuseDefaultTemplate();
      }
    }
    return await this.buildOrReuseDefaultTemplate();
  }

  /**
   * Resources the configured template asked for. The default mountable
   * template honors them too, so a repo template that falls back never
   * silently downgrades the machine — a 2 GB session's setup would OOM in
   * the 1 GB default. Per-size default templates cost one extra build each.
   */
  private requestedBuildResources(): TemplateResources | undefined {
    const spec =
      this.templateSpec && isNamedTemplateSpec(this.templateSpec) ? this.templateSpec : this._resolvedNamedSpec;
    return spec?.buildResources;
  }

  private async buildOrReuseDefaultTemplate(): Promise<string> {
    const { template, id, resources } = createDefaultMountableTemplate(this.requestedBuildResources());

    const exists = await Template.exists(id, this.connectionOpts);
    if (exists) {
      this.logger.debug(`${LOG_PREFIX} Using cached mountable template: ${id}`);
      this._resolvedTemplateId = id;
      return id;
    }

    this.logger.debug(`${LOG_PREFIX} Building default mountable template: ${id}...`);
    const buildResult = await Template.build(template as TemplateClass, id, { ...this.connectionOpts, ...resources });
    this._resolvedTemplateId = buildResult.templateId;
    this.logger.debug(`${LOG_PREFIX} Template built and cached: ${buildResult.templateId}`);
    return buildResult.templateId;
  }

  /**
   * Build the default mountable template (bypasses exists check).
   *
   * Override point: called from the template-not-found retry path in
   * `start()` when no explicit template was configured.
   */
  protected async buildDefaultTemplate(): Promise<string> {
    const { template, id, resources } = createDefaultMountableTemplate(this.requestedBuildResources());
    this.logger.debug(`${LOG_PREFIX} Building default mountable template: ${id}...`);
    const buildResult = await Template.build(template as TemplateClass, id, { ...this.connectionOpts, ...resources });
    this._resolvedTemplateId = buildResult.templateId;
    this.logger.debug(`${LOG_PREFIX} Template built: ${buildResult.templateId}`);
    return buildResult.templateId;
  }

  /**
   * Write marker file for detecting config changes on reconnect.
   * Stores both the mount path and config hash in the file.
   */
  private async writeMarkerFile(mountPath: string): Promise<void> {
    if (!this._sandbox) return;

    const markerContent = this.mounts.getMarkerContent(mountPath);
    if (!markerContent) return;

    const filename = this.mounts.markerFilename(mountPath);
    const markerPath = `/tmp/.mastra-mounts/${filename}`;
    try {
      await this._sandbox.commands.run('mkdir -p /tmp/.mastra-mounts');
      await this._sandbox.files.write(markerPath, markerContent);
    } catch {
      // Non-fatal - marker is just for optimization
      this.logger.debug(`${LOG_PREFIX} Warning: Could not write marker file at ${markerPath}`);
    }
  }

  /**
   * Check if a path is already mounted and if the config matches.
   */
  private async checkExistingMount(
    mountPath: string,
    newConfig: E2BMountConfig,
  ): Promise<'not_mounted' | 'matching' | 'mismatched'> {
    if (!this._sandbox) throw new SandboxNotReadyError(this.id);

    // Check if path is a mount point
    const mountCheck = await this._sandbox.commands.run(
      `mountpoint -q "${mountPath}" && echo "mounted" || echo "not mounted"`,
    );

    if (mountCheck.stdout.trim() !== 'mounted') {
      return 'not_mounted';
    }

    // Path is mounted - check if config matches via marker file
    const filename = this.mounts.markerFilename(mountPath);
    const markerPath = `/tmp/.mastra-mounts/${filename}`;

    try {
      const markerResult = await this._sandbox.commands.run(`cat "${markerPath}" 2>/dev/null || echo ""`);
      const parsed = this.mounts.parseMarkerContent(markerResult.stdout.trim());

      if (!parsed) {
        return 'mismatched';
      }

      // Compute hash of the NEW config and compare with stored hash
      const newConfigHash = this.mounts.computeConfigHash(newConfig);
      this.logger.debug(
        `${LOG_PREFIX} Marker check - stored hash: "${parsed.configHash}", new config hash: "${newConfigHash}"`,
      );

      if (parsed.path === mountPath && parsed.configHash === newConfigHash) {
        return 'matching';
      }
    } catch {
      // Marker doesn't exist or can't be read - treat as mismatched
    }

    return 'mismatched';
  }

  /**
   * Check if an error indicates the sandbox itself is dead/gone.
   * Does NOT include code execution timeouts (those are the user's code taking too long).
   * Does NOT include "port is not open" - that needs sandbox kill, not reconnect.
   */
  private isSandboxDeadError(error: unknown): boolean {
    if (!error) return false;
    const errorStr = String(error);
    return (
      /\b(?:paused\s+)?sandbox(?:\s+\S+)?\s+(?:was\s+)?not found\b/i.test(errorStr) ||
      errorStr.includes('Sandbox is probably not running') ||
      errorStr.includes('sandbox has been killed')
    );
  }

  /**
   * Handle sandbox timeout by clearing the instance and resetting state.
   *
   * Bypasses the normal stop() lifecycle because the sandbox is already dead —
   * we can't unmount filesystems or run cleanup commands. Instead we reset
   * mount states to 'pending' so they get re-mounted when start() runs again.
   */
  private handleSandboxTimeout(): void {
    this._sandbox = null;

    // Reset retryable entries to pending so they get re-mounted on restart.
    // A mount error belongs to the dead physical sandbox and must not prevent
    // the configured filesystem from being attempted in its replacement.
    for (const [path, entry] of this.mounts.entries) {
      if (entry.state === 'mounted' || entry.state === 'mounting' || entry.state === 'error') {
        this.mounts.set(path, { state: 'pending', error: undefined });
      }
    }

    this.status = 'stopped';
  }

  /**
   * Execute an operation with automatic retry if the sandbox is found to be dead.
   *
   * When the E2B sandbox times out or crashes mid-operation, this method
   * resets sandbox state, restarts it, and retries the operation once.
   *
   * @internal Used by E2BProcessManager (spawn) and E2BCodeModeTransport (file setup)
   * to handle dead sandboxes.
   */
  async retryOnDead<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (error) {
      if (this.isSandboxDeadError(error) && !this._isRetrying) {
        this.handleSandboxTimeout();
        this._isRetrying = true;
        try {
          await this.ensureRunning();
          return await fn();
        } finally {
          this._isRetrying = false;
        }
      }
      throw error;
    }
  }
}
